import Label from 'components/primitives/Label';
import { Field, FieldProps } from 'formik';
import { FC } from 'react';
import { WeeklyAvailabilityProps } from './WeeklyAvailability.props';

const WeeklyAvailabilityView: FC<WeeklyAvailabilityProps> = ({
  name,
  label = 'Weekly Availability',
  isRequired = false,
  tooltipMessage,
}) => {
  const days = [
    { key: '0', label: 'S', fullName: 'Sunday' },
    { key: '1', label: 'M', fullName: 'Monday' },
    { key: '2', label: 'T', fullName: 'Tuesday' },
    { key: '3', label: 'W', fullName: 'Wednesday' },
    { key: '4', label: 'T', fullName: 'Thursday' },
    { key: '5', label: 'F', fullName: 'Friday' },
    { key: '6', label: 'S', fullName: 'Saturday' },
  ];

  const timeSlots = [
    { key: 'am', label: 'AM' },
    { key: 'pm', label: 'PM' },
  ];

  return (
    <div className="w-full">
      <Label name={'dropzone-file'} isRequired={isRequired} title={label} />

      <Field name={name}>
        {({ field, form }: FieldProps) => {
          const currentValue = field.value || {};

          const handleToggle = (dayKey: string, timeSlot: string) => {
            const newValue = {
              ...currentValue,
              [dayKey]: {
                ...currentValue[dayKey],
                [timeSlot]: !currentValue[dayKey]?.[timeSlot],
              },
            };
            form.setFieldValue(name, newValue);
          };

          const WEEKDAYS = ['1', '2', '3', '4', '5'];
          const WEEKENDS = ['0', '6'];
          const ALL_DAYS = ['0', '1', '2', '3', '4', '5', '6'];

          const isFullyOn = (keys: string[]) =>
            keys.every((k) => currentValue[k]?.am && currentValue[k]?.pm);

          const applyBulkToggle = (keys: string[]) => {
            const turnOn = !isFullyOn(keys);
            const next = { ...currentValue };
            keys.forEach((k) => {
              next[k] = { am: turnOn, pm: turnOn };
            });
            form.setFieldValue(name, next);
          };

          const bulkButtonClass = (keys: string[]) =>
            `flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              isFullyOn(keys)
                ? 'bg-green-200 text-gray-700 hover:bg-green-600'
                : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
            }`;

          return (
            <div className="p-4">
              {/* Bulk-select shortcuts */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => applyBulkToggle(ALL_DAYS)}
                  className={bulkButtonClass(ALL_DAYS)}>
                  All
                </button>
                <button
                  type="button"
                  onClick={() => applyBulkToggle(WEEKDAYS)}
                  className={bulkButtonClass(WEEKDAYS)}>
                  Weekdays
                </button>
                <button
                  type="button"
                  onClick={() => applyBulkToggle(WEEKENDS)}
                  className={bulkButtonClass(WEEKENDS)}>
                  Weekends
                </button>
              </div>

              {/* Days grid */}
              <div className="space-y-2">
                {days.map((day) => (
                  <div
                    key={day.key}
                    className="grid grid-cols-10 gap-3 items-center">
                    <div className="text-[18px] font-medium text-gray-700 text-center col-span-2">
                      {day.label}
                    </div>
                    {timeSlots.map((slot) => {
                      const isActive =
                        currentValue[day.key]?.[slot.key] || false;
                      return (
                        <button
                          key={`${day.key}-${slot.key}`}
                          type="button"
                          onClick={() => handleToggle(day.key, slot.key)}
                          className={`
                            w-full h-[70px] rounded-md text-md font-medium transition-colors col-span-4
                            ${
                              isActive
                                ? 'bg-green-200 text-gray-700 hover:bg-green-600'
                                : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                            }
                          `}>
                          {slot.label}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              <p className="text-md text-gray-500 mt-3 text-center">
                Click to enable or disable
              </p>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default WeeklyAvailabilityView;
